import { useState } from "react";
import { api } from "../../lib/api";
import { t, statusLabel } from "../../lib/i18n";
import { toast } from "sonner";
import { PrizeBreakdownEditor } from "../CreateTournament";
import { Field, Modal, MAX_PLAYERS_OPTS } from "./_shared";

export default function TournamentAdmin({ tournaments, players, activeTour, setActiveTour, matches, loadMatches, refresh }) {
  const [form, setForm] = useState(null);
  const [subsTour, setSubsTour] = useState(null);
  const [inviteTour, setInviteTour] = useState(null);
  const [refereesTour, setRefereesTour] = useState(null);

  const detectTies = async (id) => {
    try {
      const r = await api.post(`/admin/tournaments/${id}/detect-ties`);
      if ((r.data?.created || 0) > 0) {
        toast.success(`${t.tieBreakersGenerated}${r.data.created}`);
        refresh();
      } else {
        toast.info(t.noTies);
      }
    } catch (_) { toast.error(t.failed); }
  };

  const toggleLeaderboard = async (id) => {
    try {
      const r = await api.post(`/admin/tournaments/${id}/toggle-leaderboard`);
      toast.success(
        r.data.included_in_leaderboard
          ? t.leaderboardIncludedToast
          : t.leaderboardExcludedToast,
      );
      refresh();
    } catch (_) { toast.error(t.failed); }
  };

  const empty = {
    name: "", slug: "", description: "", location: "",
    start_date: new Date().toISOString().slice(0, 10),
    status: "upcoming", prize_pool: "", prize_breakdown: [],
    banner_url: "https://images.pexels.com/photos/14266493/pexels-photo-14266493.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    format: "single_elim", max_players: 8, player_ids: [],
    num_groups: 0, advancers_per_group: 2,
  };

  const save = async () => {
    try {
      const payload = { ...form, start_date: new Date(form.start_date).toISOString() };
      if (form.id) await api.put(`/admin/tournaments/${form.id}`, payload);
      else await api.post(`/admin/tournaments`, payload);
      toast.success(t.saved);
      setForm(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || t.failed);
    }
  };

  const seedBracket = async (id) => {
    try {
      const r = await api.post(`/admin/tournaments/${id}/seed-bracket`);
      toast.success(`${t.bracketCreated}: ${r.data.count}`);
      setActiveTour(id);
    } catch (e) { toast.error(e.response?.data?.detail || t.failed); }
  };

  const shuffle = async (id) => {
    try {
      await api.post(`/admin/tournaments/${id}/shuffle`);
      toast.success(t.shuffled);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || t.failed); }
  };

  const generateKnockout = async (id) => {
    try {
      const r = await api.post(`/admin/tournaments/${id}/generate-knockout`);
      toast.success(`${t.knockoutGenerated}: ${r.data.count}`);
      setActiveTour(id);
    } catch (e) { toast.error(e.response?.data?.detail || t.failed); }
  };

  const remove = async (id) => {
    if (!window.confirm(t.confirmDelete)) return;
    await api.delete(`/admin/tournaments/${id}`);
    toast.success(t.deleted);
    refresh();
  };

  return (
    <div className="mt-8">
      <button
        onClick={() => setForm(empty)}
        data-testid="new-tournament-btn"
        className="px-5 py-2.5 bg-[#FF0055] hover:bg-[#FF3377] text-white font-bold uppercase tracking-[0.25em] text-xs"
      >
        {t.newTournament}
      </button>

      <div className="mt-6 grid lg:grid-cols-2 gap-4">
        {tournaments.map((tt) => (
          <div key={tt.id} className="border border-white/10 bg-[#0C0C14] p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-[0.25em] font-mono text-[#00E5FF]">
                    {statusLabel(tt.status)}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.25em] font-mono text-white/40">
                    {tt.format === "group_knockout" ? t.formatGroupKnockout : t.formatSingleElim}
                  </span>
                </div>
                <div className="font-display font-black uppercase italic text-2xl mt-1 truncate">{tt.name}</div>
                <div className="text-xs text-white/50 mt-1 font-mono">
                  {tt.location} · {tt.player_ids.length} {t.players.toLowerCase()}
                </div>
              </div>
              <div className="flex flex-col gap-1.5 text-[10px] uppercase tracking-[0.25em] font-bold flex-shrink-0">
                <button onClick={() => setForm({ ...tt, start_date: tt.start_date.slice(0, 10) })} className="text-white/70 hover:text-[#00E5FF]">{t.edit}</button>
                <button onClick={() => shuffle(tt.id)} data-testid={`shuffle-${tt.id}`} className="text-white/70 hover:text-[#E2FF00]">{t.shuffle}</button>
                <button onClick={() => seedBracket(tt.id)} className="text-white/70 hover:text-[#E2FF00]">{t.bracketBtn}</button>
                {tt.format === "group_knockout" && (
                  <button onClick={() => generateKnockout(tt.id)} className="text-white/70 hover:text-[#E2FF00]">{t.generateKnockout}</button>
                )}
                <button onClick={() => setActiveTour(tt.id)} className="text-white/70 hover:text-[#FF0055]">{t.matches}</button>
                <button onClick={() => setSubsTour(tt.id)} data-testid={`subs-${tt.id}`} className="text-white/70 hover:text-[#00E5FF]">{t.substitutes}</button>
                <button onClick={() => setInviteTour(tt.id)} data-testid={`invite-${tt.id}`} className="text-white/70 hover:text-[#00E5FF]">{t.invite}</button>
                <button onClick={() => setRefereesTour(tt.id)} data-testid={`referees-${tt.id}`} className="text-white/70 hover:text-[#9B7AFF]">{t.referees}</button>
                <button onClick={() => detectTies(tt.id)} data-testid={`detect-ties-${tt.id}`} className="text-white/70 hover:text-[#FF0055]">{t.detectTies}</button>
                <button
                  onClick={() => toggleLeaderboard(tt.id)}
                  data-testid={`toggle-leaderboard-${tt.id}`}
                  className={`text-xs uppercase tracking-[0.2em] font-bold ${
                    tt.included_in_leaderboard ? "text-[#E2FF00]" : "text-white/50 hover:text-[#E2FF00]"
                  }`}
                  title={tt.included_in_leaderboard ? t.leaderboardIncluded : t.leaderboardNotIncluded}
                >
                  ★ {tt.included_in_leaderboard ? t.leaderboardOn : t.leaderboardOff}
                </button>
                <button onClick={() => remove(tt.id)} className="text-[#FF0055]">{t.delete}</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {form && (
        <Modal title={form.id ? t.editTournament : t.newTournament} onClose={() => setForm(null)}>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t.tournamentName} value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <Field label={t.slug} value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} />
            <Field label={t.location} value={form.location} onChange={(v) => setForm({ ...form, location: v })} />
            <Field label={t.prizePool} value={form.prize_pool} onChange={(v) => setForm({ ...form, prize_pool: v })} />
            <Field label={t.startDate} type="date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} />
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40">{t.maxPlayers}</label>
              <select value={form.max_players} onChange={(e) => setForm({ ...form, max_players: parseInt(e.target.value) })}
                className="w-full bg-transparent border border-white/15 px-3 py-2 mt-1 focus:outline-none focus:border-[#00E5FF] text-sm">
                {MAX_PLAYERS_OPTS.map((n) => (
                  <option key={n} className="bg-[#0C0C14]" value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40">{t.format}</label>
              <select value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })}
                className="w-full bg-transparent border border-white/15 px-3 py-2 mt-1 focus:outline-none focus:border-[#00E5FF] text-sm">
                <option className="bg-[#0C0C14]" value="single_elim">{t.formatSingleElim}</option>
                <option className="bg-[#0C0C14]" value="double_elim">{t.formatDoubleElim}</option>
                <option className="bg-[#0C0C14]" value="group_knockout">{t.formatGroupKnockout}</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40">{t.status}</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full bg-transparent border border-white/15 px-3 py-2 mt-1 focus:outline-none focus:border-[#00E5FF] text-sm">
                <option className="bg-[#0C0C14]" value="upcoming">{t.statusUpcoming}</option>
                <option className="bg-[#0C0C14]" value="live">{t.statusLive}</option>
                <option className="bg-[#0C0C14]" value="completed">{t.statusCompleted}</option>
              </select>
            </div>
            {form.format === "group_knockout" && (
              <>
                <Field type="number" label={t.numGroups} value={form.num_groups}
                  onChange={(v) => setForm({ ...form, num_groups: parseInt(v) || 0 })} />
                <Field type="number" label={t.advancersPerGroup} value={form.advancers_per_group}
                  onChange={(v) => setForm({ ...form, advancers_per_group: parseInt(v) || 1 })} />
              </>
            )}
            <Field label={t.bannerUrl} full value={form.banner_url} onChange={(v) => setForm({ ...form, banner_url: v })} />
            <div className="col-span-2">
              <PrizeBreakdownEditor
                value={form.prize_breakdown || []}
                onChange={(v) => setForm({ ...form, prize_breakdown: v })}
              />
            </div>
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40">{t.description}</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3} className="w-full bg-transparent border border-white/15 px-3 py-2 mt-1 text-sm focus:outline-none focus:border-[#00E5FF]" />
            </div>
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 mb-2 block">
                {t.players} ({form.player_ids.length})
              </label>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-1.5 max-h-60 overflow-y-auto border border-white/10 p-2">
                {players.map((p) => {
                  const checked = form.player_ids.includes(p.id);
                  return (
                    <label key={p.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-white/5 px-2 py-1">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const ids = checked
                            ? form.player_ids.filter((x) => x !== p.id)
                            : [...form.player_ids, p.id];
                          setForm({ ...form, player_ids: ids });
                        }}
                      />
                      <span>{p.handle}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setForm(null)} className="px-4 py-2 text-xs uppercase tracking-[0.25em] font-bold text-white/60">{t.cancel}</button>
            <button onClick={save} data-testid="save-tournament-btn" className="px-5 py-2 bg-[#FF0055] hover:bg-[#FF3377] text-white font-bold uppercase tracking-[0.25em] text-xs">{t.save}</button>
          </div>
        </Modal>
      )}

      {activeTour && (
        <MatchesEditor
          tournamentId={activeTour}
          matches={matches}
          players={players}
          reload={loadMatches}
          onClose={() => setActiveTour(null)}
        />
      )}

      {subsTour && (
        <SubsEditor
          tournament={tournaments.find((x) => x.id === subsTour)}
          players={players}
          refresh={refresh}
          onClose={() => setSubsTour(null)}
        />
      )}

      {inviteTour && (
        <InviteEditor
          tournament={tournaments.find((x) => x.id === inviteTour)}
          players={players}
          onClose={() => setInviteTour(null)}
        />
      )}

      {refereesTour && (
        <RefereesEditor
          tournament={tournaments.find((x) => x.id === refereesTour)}
          players={players}
          refresh={refresh}
          onClose={() => setRefereesTour(null)}
        />
      )}
    </div>
  );
}

function RefereesEditor({ tournament, players, refresh, onClose }) {
  const [picked, setPicked] = useState(new Set(tournament?.referee_player_ids || []));
  const [busy, setBusy] = useState(false);
  const toggle = (pid) => {
    const next = new Set(picked);
    if (next.has(pid)) next.delete(pid); else next.add(pid);
    setPicked(next);
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/tournaments/${tournament.id}/referees`, {
        referee_player_ids: Array.from(picked),
      });
      toast.success(t.refereesUpdated);
      onClose();
      refresh();
    } catch (_) { toast.error(t.failed); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`${t.referees} · ${tournament?.name || ""}`} onClose={onClose}>
      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {players.map((p) => (
          <label
            key={p.id}
            data-testid={`ref-toggle-${p.id}`}
            className="flex items-center gap-3 border border-white/10 px-3 py-2 cursor-pointer hover:border-white/20"
          >
            <input type="checkbox" checked={picked.has(p.id)} onChange={() => toggle(p.id)} />
            <span className="flex-1 text-sm">{p.handle}</span>
            <span className="text-[10px] font-mono text-white/40">{p.country || ""}</span>
          </label>
        ))}
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 border border-white/20 text-white/70 text-xs uppercase tracking-[0.25em] font-bold">{t.cancel}</button>
        <button onClick={save} disabled={busy} data-testid="ref-save"
          className="px-4 py-2 bg-[#9B7AFF] hover:bg-[#B299FF] text-white text-xs uppercase tracking-[0.25em] font-bold disabled:opacity-50">{t.save}</button>
      </div>
    </Modal>
  );
}

function SubsEditor({ tournament, players, refresh, onClose }) {
  const [pickToAdd, setPickToAdd] = useState("");
  const [swapOut, setSwapOut] = useState("");
  const [swapIn, setSwapIn] = useState("");
  const tt = tournament;
  const subIds = tt?.sub_player_ids || [];
  const rosterIds = tt?.player_ids || [];
  const playerById = Object.fromEntries(players.map((p) => [p.id, p]));
  const eligibleForSub = players.filter((p) => !rosterIds.includes(p.id) && !subIds.includes(p.id));

  const addSub = async () => {
    if (!pickToAdd) return;
    try {
      await api.post(`/admin/tournaments/${tt.id}/subs/add`, { player_id: pickToAdd });
      toast.success(t.saved); setPickToAdd(""); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || t.failed); }
  };
  const removeSub = async (pid) => {
    try {
      await api.delete(`/admin/tournaments/${tt.id}/subs/${pid}`);
      toast.success(t.deleted); refresh();
    } catch (_) { toast.error(t.failed); }
  };
  const swap = async () => {
    if (!swapOut || !swapIn) return;
    try {
      await api.post(`/admin/tournaments/${tt.id}/swap-player`, { out_player_id: swapOut, in_player_id: swapIn });
      toast.success(t.swappedToast); setSwapOut(""); setSwapIn(""); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || t.failed); }
  };

  return (
    <Modal title={`${t.substitutes} · ${tt?.name || ""}`} onClose={onClose}>
      <div className="space-y-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#00E5FF] font-mono mb-3">
            // {t.substitutes} ({subIds.length})
          </div>
          {subIds.length === 0 ? (
            <p className="text-xs text-white/40 font-mono">{t.noSubs}</p>
          ) : (
            <div className="space-y-1.5">
              {subIds.map((pid) => (
                <div key={pid} className="flex items-center justify-between border border-white/10 px-3 py-2 text-sm">
                  <span>{playerById[pid]?.handle || "—"}</span>
                  <button onClick={() => removeSub(pid)} data-testid={`del-sub-${pid}`}
                    className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#FF0055]">{t.removeSub}</button>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2 mt-3">
            <select value={pickToAdd} onChange={(e) => setPickToAdd(e.target.value)} data-testid="sub-add-select"
              className="flex-1 bg-transparent border border-white/15 px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF]">
              <option value="" className="bg-[#0C0C14]">— {t.none} —</option>
              {eligibleForSub.map((p) => (
                <option key={p.id} value={p.id} className="bg-[#0C0C14]">{p.handle}</option>
              ))}
            </select>
            <button onClick={addSub} data-testid="add-sub-btn"
              className="px-4 py-2 bg-[#FF0055] hover:bg-[#FF3377] text-white font-bold uppercase tracking-[0.2em] text-xs">{t.addSub}</button>
          </div>
        </div>

        <div className="border-t border-white/10 pt-5">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF0055] font-mono mb-3">
            // {t.swapPlayer}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 block mb-1">{t.swapPlayer}</label>
              <select value={swapOut} onChange={(e) => setSwapOut(e.target.value)} data-testid="swap-out"
                className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF]">
                <option value="" className="bg-[#0C0C14]">— {t.none} —</option>
                {rosterIds.map((pid) => (
                  <option key={pid} value={pid} className="bg-[#0C0C14]">{playerById[pid]?.handle || pid}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 block mb-1">{t.pickIncoming}</label>
              <select value={swapIn} onChange={(e) => setSwapIn(e.target.value)} data-testid="swap-in"
                className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF]">
                <option value="" className="bg-[#0C0C14]">— {t.none} —</option>
                {subIds.map((pid) => (
                  <option key={pid} value={pid} className="bg-[#0C0C14]">{playerById[pid]?.handle || pid} ({t.substitutes})</option>
                ))}
              </select>
            </div>
          </div>
          <button onClick={swap} disabled={!swapOut || !swapIn} data-testid="confirm-swap"
            className="mt-3 px-4 py-2 bg-[#FF0055] hover:bg-[#FF3377] disabled:opacity-50 text-white font-bold uppercase tracking-[0.2em] text-xs">{t.swapPlayer}</button>
        </div>
      </div>
    </Modal>
  );
}

function InviteEditor({ tournament, players, onClose }) {
  const tt = tournament;
  const rosterIds = tt?.player_ids || [];
  const candidates = players.filter((p) => !rosterIds.includes(p.id) && p.user_id);
  const send = async (pid) => {
    try {
      await api.post(`/admin/tournaments/${tt.id}/invite`, { player_id: pid });
      toast.success(t.invited);
    } catch (e) { toast.error(e.response?.data?.detail || t.failed); }
  };
  return (
    <Modal title={`${t.inviteToTournament} · ${tt?.name || ""}`} onClose={onClose}>
      <p className="text-xs text-white/50 font-mono mb-3">
        Lista zawodników z kontem · {candidates.length}
      </p>
      <div className="max-h-[60vh] overflow-y-auto space-y-1.5">
        {candidates.map((p) => (
          <div key={p.id} className="flex items-center justify-between border border-white/10 px-3 py-2 text-sm">
            <span>{p.handle} <span className="text-white/40 text-xs">· {p.country}</span></span>
            <button onClick={() => send(p.id)} data-testid={`invite-send-${p.id}`}
              className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#00E5FF] hover:underline">{t.invite}</button>
          </div>
        ))}
        {candidates.length === 0 && (
          <p className="text-center text-white/40 font-mono text-xs py-8">{t.none}</p>
        )}
      </div>
    </Modal>
  );
}

function MatchesEditor({ tournamentId, matches, players, reload, onClose }) {
  const playerName = (id) => players.find((p) => p.id === id)?.handle || "—";
  const submit = async (m) => {
    try {
      const winner_id = m.winner_id || (m.score_a > m.score_b ? m.player_a_id : m.player_b_id);
      await api.put(`/admin/matches/${m.id}`, {
        score_a: m.score_a, score_b: m.score_b, winner_id, status: "completed",
      });
      toast.success(t.saved); reload();
    } catch (_) { toast.error(t.failed); }
  };
  const grouped = matches.reduce((acc, m) => {
    const key = m.stage === "group" ? `${t.group} ${String.fromCharCode(65 + (m.group_index ?? 0))}` : t.knockout;
    (acc[key] = acc[key] || []).push(m);
    return acc;
  }, {});
  return (
    <Modal title={t.matchResults} onClose={onClose}>
      <div className="max-h-[60vh] overflow-y-auto space-y-4">
        {Object.entries(grouped).map(([sectionLabel, ms]) => (
          <div key={sectionLabel}>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#00E5FF] mb-2 font-mono">
              {sectionLabel}
            </div>
            <div className="space-y-2">
              {ms.map((m) => (
                <MatchRow key={m.id} match={m} playerName={playerName} onSubmit={submit} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}

function MatchRow({ match, playerName, onSubmit }) {
  const [sa, setSa] = useState(match.score_a || 0);
  const [sb, setSb] = useState(match.score_b || 0);
  return (
    <div className="border border-white/10 p-3 grid grid-cols-12 gap-2 items-center">
      <span className="col-span-1 text-[10px] font-mono text-white/40">R{match.round}.{match.slot}</span>
      <span className="col-span-3 text-sm truncate">{playerName(match.player_a_id)}</span>
      <input type="number" value={sa} onChange={(e) => setSa(parseInt(e.target.value) || 0)} className="col-span-1 bg-transparent border border-white/15 px-2 py-1 text-center text-sm" />
      <span className="col-span-1 text-center text-white/30">vs</span>
      <input type="number" value={sb} onChange={(e) => setSb(parseInt(e.target.value) || 0)} className="col-span-1 bg-transparent border border-white/15 px-2 py-1 text-center text-sm" />
      <span className="col-span-3 text-sm truncate">{playerName(match.player_b_id)}</span>
      <button
        onClick={() => onSubmit({ ...match, score_a: sa, score_b: sb })}
        disabled={!match.player_a_id || !match.player_b_id}
        data-testid={`save-match-${match.id}`}
        className="col-span-2 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold bg-[#00E5FF]/15 text-[#00E5FF] border border-[#00E5FF]/40 hover:bg-[#00E5FF]/25 disabled:opacity-30"
      >
        {match.status === "completed" ? t.recordUpdate : t.record}
      </button>
    </div>
  );
}
